/**
 * Pure helpers for an ordered field selection. The order is the column order in the
 * explorer grid and the column order of a pull, so the selection is a list, never a set:
 * every helper returns a new array with no duplicates and no blank paths.
 */

export function normalizeSelection(paths: Iterable<string>): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const path of paths) {
    const trimmed = path.trim()
    if (trimmed === "" || seen.has(trimmed)) continue
    seen.add(trimmed)
    result.push(trimmed)
  }
  return result
}

/** The selection with `path` added at the end, or removed */
export function withField(selection: readonly string[], path: string, selected: boolean): string[] {
  const without = selection.filter((existing) => existing !== path)
  return selected ? [...without, path] : without
}

/**
 * Moves `path` so it sits immediately before `beforePath`, or last when `beforePath`
 * is null. Unknown paths leave the selection unchanged.
 */
export function moveField(selection: readonly string[], path: string, beforePath: string | null): string[] {
  if (path === beforePath || !selection.includes(path)) return [...selection]
  if (beforePath !== null && !selection.includes(beforePath)) return [...selection]
  const without = selection.filter((existing) => existing !== path)
  const index = beforePath === null ? without.length : without.indexOf(beforePath)
  return [...without.slice(0, index), path, ...without.slice(index)]
}

const STORAGE_PREFIX = "ats-explorer:fields"
const FROZEN_PREFIX = "ats-explorer:frozen"

/**
 * One entry per ATS source and entity. The source is part of the key because two sources
 * on the same vendor expose different fields, so their columns must not be shared.
 */
export function fieldSelectionStorageKey(atsSystem: string, atsSource: string, entityName: string): string {
  return `${STORAGE_PREFIX}:${atsSystem}:${atsSource}:${entityName}`
}

/** The frozen-column boundary is remembered per source and entity, like the columns are */
export function frozenColumnsStorageKey(atsSystem: string, atsSource: string, entityName: string): string {
  return `${FROZEN_PREFIX}:${atsSystem}:${atsSource}:${entityName}`
}

export type SelectionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">

/** The remembered selection, or null when nothing valid is stored */
export function readStoredSelection(storage: SelectionStorage | null, key: string): string[] | null {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === "string")) return null
    const selection = normalizeSelection(parsed)
    return selection.length > 0 ? selection : null
  } catch {
    return null
  }
}

export function writeStoredSelection(storage: SelectionStorage | null, key: string, selection: readonly string[]): void {
  try {
    if (selection.length === 0) storage?.removeItem(key)
    else storage?.setItem(key, JSON.stringify(selection))
  } catch {
    // Storage can be unavailable (private mode, quota); the selection still applies for the session
  }
}

/**
 * The page's sessionStorage or localStorage; null during static rendering, in tests, and
 * when the embedding policy blocks storage — reading the property itself throws there.
 */
export function browserStorage(kind: "session" | "local"): SelectionStorage | null {
  try {
    if (typeof window === "undefined") return null
    return kind === "session" ? window.sessionStorage : window.localStorage
  } catch {
    return null
  }
}

/** The explorer's columns live for the tab only */
export function sessionSelectionStorage(): SelectionStorage | null {
  return browserStorage("session")
}


/** How many leading columns were frozen, or 0 when none were or the entry is unusable */
export function readStoredFrozenColumns(storage: SelectionStorage | null, key: string): number {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return 0
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === "number" && Number.isInteger(parsed) && parsed > 0 ? parsed : 0
  } catch {
    return 0
  }
}

export function writeStoredFrozenColumns(storage: SelectionStorage | null, key: string, count: number): void {
  try {
    if (count > 0) storage?.setItem(key, JSON.stringify(count))
    else storage?.removeItem(key)
  } catch {
    // A browser with storage disabled just does not remember the boundary
  }
}
